import { tapComplete } from "@calaosoft/osapp-common/rxjs/operators/tap-complete";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { TaskBase } from "@calaosoft/osapp-mobile/modules/bts/models/task-base";
import { TaskDescriptor } from "@calaosoft/osapp-mobile/modules/bts/models/task-descriptor";
import { CustomerTourService } from "@calaosoft/osapp-mobile/modules/customer-tour/services/customer-tour.service";
import { IStock } from "@calaosoft/osapp-mobile/modules/logistics/stock/models/istock";
import { StockService } from "@calaosoft/osapp-mobile/modules/logistics/stock/services/stock.service";
import { combineLatest, Observable } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { EMerchFlag } from "../emerch-flag";

/** Supprime tous les stocks clients obsolètes de l'appareil. */
export class RemoveObsoleteCustomerHistoryTask extends TaskBase {
	//#region FIELDS

	private readonly msvcCustomerTour: CustomerTourService;
	private readonly msvcStock: StockService;

	//#endregion FIELDS

	//#region METHODS

	constructor(poDescriptor: TaskDescriptor) {
		super(poDescriptor);

		this.msvcCustomerTour = poDescriptor.ioInjector.get(CustomerTourService);
		this.msvcStock = poDescriptor.ioInjector.get(StockService);
	}

	/** @override */
	protected override execTask$(): Observable<void> {
		return combineLatest([this.msvcCustomerTour.getCustomerIdsAsync(), this.msvcStock.getAllStocksAsync()]).pipe(
			mergeMap(([paCustomerIds, paStocks]: [string[], IStock[]]) =>
				this.msvcStock.removeStocksAsync(this.getToRemoveStocks(paCustomerIds, paStocks))
			),
			tapComplete(() => this.msvcFlag!.setFlagValue(EMerchFlag.obsoleteStocksRemoved, true))
		);
	}

	private getToRemoveStocks(paCustomerIds: string[], paStocks: IStock[]): IStock[] {
		const laToRemoveStocks: IStock[] = [];
		const laWarehouseIds: string[] = paCustomerIds.map((psCustomerId: string) =>
			this.msvcStock!.extractWarehouseIdFromCustomerId(psCustomerId)
		);

		// On groupe les stocks par 'warehouseId' pour faciliter la suppression en cas de plusieurs version pour un même 'warehouseId'.
		ArrayHelper.groupBy(paStocks, (poStock: IStock) => poStock.warehouseId).forEach(
			(paGroupedStocks: IStock[], psStockWarehouseId: string) => {
				// Si tous les identifiants d'entrepôts sont différents de l'identifiant de l'entrepôt courant, il faut supprimer tous les stocks associés.
				if (laWarehouseIds.every((psWarehouseId: string) => psWarehouseId !== psStockWarehouseId))
					laToRemoveStocks.push(...paGroupedStocks);
				else if (paGroupedStocks.length > 1) {
					// Suppression des versions obsolètes.
					// On trie les stocks par version de la plus ancienne à la plus récente.
					const laSortedGroupedStocks: IStock[] = ArrayHelper.dynamicSort(paGroupedStocks, "version");
					// On supprime le dernier car c'est celui avec la version à jour, on veut le conserver.
					laSortedGroupedStocks.pop();
					laToRemoveStocks.push(...laSortedGroupedStocks);
				}
				// Sinon : un seul stock associé à un entrepôt utilisé, on le conserve.
			}
		);

		return laToRemoveStocks;
	}

	//#endregion METHODS
}
